import { useInfiniteQuery } from "@tanstack/react-query";
import { Offcanvas, Button, Form, Spinner, InputGroup, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faMapMarkerAlt,
  faCity,
  faCheck,
  faTimes,
  faInfoCircle,
  faChevronLeft
} from "@fortawesome/free-solid-svg-icons";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { MunicipioType } from "data/municipio";
import debounce from "lodash/debounce";
import InfiniteLoader from "react-window-infinite-loader";
import { FixedSizeList as List } from "react-window";

interface MunicipioSelectorProps {
  show: boolean;
  onHide: () => void;
  onSelect: (municipio: MunicipioType) => void;
  title?: string;
  initialUf?: string; // Optional initial UF filter
}

// Função para buscar municípios com paginação para InfiniteQuery
const fetchMunicipios = async ({ pageParam = 1 }, filter?: string, uf?: string) => {
  let url = "/api/municipio";
  const pageSize = 30; // Aumentamos o tamanho da página para melhor performance
  const params = new URLSearchParams();

  if (filter) params.append("filter", filter);
  if (uf) params.append("uf", uf);
  params.append("pageIndex", pageParam.toString());
  params.append("pageSize", pageSize.toString());

  url += `?${params.toString()}`;

  try {
    const response = await axios.get(url);
    const result = response.data;

    // Verificar se a resposta foi bem-sucedida
    if (!result.isSuccess) {
      console.error("API returned error:", result.message);
      return {
        data: [],
        totalCount: 0,
        pageIndex: pageParam,
        pageSize,
        totalPages: 0,
        isSuccess: false,
        nextPage: undefined
      };
    }

    // Acessar os dados da paginação dentro do result.data (que é um PageResult)
    const pageResult = result.data;
    const currentPage = Number(pageResult.pageIndex);
    const totalPages = pageResult.total > 0 ? Math.ceil(pageResult.total / pageResult.pageSize) : 0;
    const nextPage = currentPage < totalPages ? currentPage + 1 : undefined;

    return {
      data: pageResult.data || [],
      totalCount: pageResult.total || 0,
      pageIndex: currentPage,
      pageSize: pageResult.pageSize || pageSize,
      totalPages,
      isSuccess: true,
      nextPage
    };
  } catch (error) {
    console.error("Error fetching municípios:", error);
    return {
      data: [],
      totalCount: 0,
      pageIndex: pageParam,
      pageSize,
      totalPages: 0,
      isSuccess: false,
      nextPage: undefined
    };
  }
};

const MunicipioSelector = ({
  show,
  onHide,
  onSelect,
  title = "Selecionar Município",
  initialUf = ""
}: MunicipioSelectorProps) => {
  // State for filters and selection
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUF, setSelectedUF] = useState<string>(initialUf);
  const [selectedMunicipio, setSelectedMunicipio] = useState<MunicipioType | null>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const listContainerRef = useRef<HTMLDivElement>(null);

  // Debouncear a busca para evitar muitas requisições durante a digitação
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setSearchTerm(term);
    }, 300),
    []
  );

  // Reset seleção quando o offcanvas é aberto
  useEffect(() => {
    if (show) {
      setSelectedMunicipio(null);
      // Iniciar com a busca vazia para mostrar resultados imediatamente
      setSearchTerm("");
      setSearchInputValue("");
      setSelectedUF(initialUf);
      setIsInitialized(true);
    }
  }, [show, initialUf]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  // Update UF and reset results
  const handleUfChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUF(e.target.value);
    // Vai refazer a query automaticamente quando a chave da query mudar
  };

  // InfiniteQuery para carregamento sob demanda
  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ["municipios", searchTerm, selectedUF],
    queryFn: (context) => fetchMunicipios(context, searchTerm, selectedUF),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    enabled: show && (isInitialized || searchTerm.length >= 2 || searchTerm.length === 0),
    staleTime: 60000, // 1 minuto
    refetchOnWindowFocus: false,
  });

  // Inicializa a busca quando o offcanvas é exibido ou o UF/termo muda
  useEffect(() => {
    if (show) {
      refetch();
      if (listContainerRef.current) {
        listContainerRef.current.scrollTop = 0;
      }
    }
  }, [show, searchTerm, selectedUF, refetch]);

  // Preparando todos os itens para renderização
  const allMunicipios = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const handleSelect = (municipio: MunicipioType) => {
    setSelectedMunicipio(municipio);
  };

  const handleConfirm = () => {
    if (selectedMunicipio) {
      onSelect(selectedMunicipio);
      onHide();
    }
  };

  // Funções para o InfiniteLoader
  const itemCount = hasNextPage ? allMunicipios.length + 1 : allMunicipios.length;

  const isItemLoaded = (index: number) => {
    return !hasNextPage || index < allMunicipios.length;
  };

  const loadMoreItems = async () => {
    if (isFetchingNextPage) return;
    await fetchNextPage();
  };

  // Renderizador de linha para a lista virtualizada
  const ItemRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    // Se estamos carregando o último item
    if (!isItemLoaded(index)) {
      return (
        <div style={style} className="p-3 text-center">
          <Spinner animation="border" size="sm" variant="primary" />
        </div>
      );
    }

    const municipio = allMunicipios[index];
    const isSelected = selectedMunicipio?.id === municipio.id;

    return (
      <div
        key={municipio.id}
        style={{
          ...style,
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          borderBottom: "1px solid #dee2e6",
          padding: "0",
        }}
        onClick={() => handleSelect(municipio)}
        className="list-group-item-action"
      >
        <div className="d-flex align-items-center w-100" style={{ padding: "12px 16px" }}>
          <div className="d-flex flex-column flex-grow-1">
            <div className="fw-medium">{municipio.nome}</div>
            <small className="text-muted">
              UF: {municipio.uf}
            </small>
          </div>
          <div className="ms-2">
            {isSelected && (
              <FontAwesomeIcon icon={faCheck} className="text-primary" />
            )}
          </div>
        </div>
      </div>
    );
  };

  // Lista de UFs brasileiras para filtrar
  const ufOptions = [
    { value: "", label: "Todas" },
    { value: "AC", label: "AC" },
    { value: "AL", label: "AL" },
    { value: "AM", label: "AM" },
    { value: "AP", label: "AP" },
    { value: "BA", label: "BA" },
    { value: "CE", label: "CE" },
    { value: "DF", label: "DF" },
    { value: "ES", label: "ES" },
    { value: "GO", label: "GO" },
    { value: "MA", label: "MA" },
    { value: "MG", label: "MG" },
    { value: "MS", label: "MS" },
    { value: "MT", label: "MT" },
    { value: "PA", label: "PA" },
    { value: "PB", label: "PB" },
    { value: "PE", label: "PE" },
    { value: "PI", label: "PI" },
    { value: "PR", label: "PR" },
    { value: "RJ", label: "RJ" },
    { value: "RN", label: "RN" },
    { value: "RO", label: "RO" },
    { value: "RR", label: "RR" },
    { value: "RS", label: "RS" },
    { value: "SC", label: "SC" },
    { value: "SE", label: "SE" },
    { value: "SP", label: "SP" },
    { value: "TO", label: "TO" }
  ];

  // Calcular a altura disponível para a lista
  const listHeight = useMemo(() => {
    return 600; // Altura fixa para o componente List
  }, []);

  // Estilo CSS para o componente Offcanvas de municípios
  const offcanvasStyle = {
    width: "550px",
    maxWidth: "95%"
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      style={offcanvasStyle}
    >
      <Offcanvas.Header className="border-bottom py-3">
        <div className="d-flex align-items-center w-100 justify-content-between">
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              className="me-2 p-1 text-body-secondary border-0"
              onClick={onHide}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
            <Offcanvas.Title className="ms-1 h4 mb-0 fw-bold">
              <FontAwesomeIcon icon={faCity} className="me-2" />
              {title}
            </Offcanvas.Title>
          </div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="p-0 d-flex flex-column h-100">
        <div className="p-3 border-bottom bg-body">
          <p className="mb-3 small text-muted">
            Selecione o município para preencher automaticamente os dados no formulário.
          </p>

          <InputGroup className="shadow-sm rounded overflow-hidden">
            <InputGroup.Text className="bg-white border-end-0 ps-3">
              <FontAwesomeIcon icon={faSearch} className="text-primary" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar município por nome..."
              onChange={handleSearchChange}
              value={searchInputValue}
              className="border-start-0 py-2"
              autoFocus
            />
            {searchInputValue && (
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setSearchInputValue("");
                  setSearchTerm("");
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            )}
            <Form.Select
              value={selectedUF}
              onChange={handleUfChange}
              style={{ width: "100px", flex: "0 0 100px" }}
            >
              {ufOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </InputGroup>

          <div className="d-flex align-items-center text-muted small mt-2">
            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
            <span>
              {selectedUF
                ? `Filtrando por UF: ${selectedUF}`
                : "Mostrando todos os municípios"}
              {searchTerm && ` com termo "${searchTerm}"`}
            </span>
          </div>
        </div>

        <div className="overflow-auto flex-grow-1" ref={listContainerRef}>
          {isLoading && !data ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
              <p className="mt-2">Carregando municípios...</p>
            </div>
          ) : allMunicipios.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <FontAwesomeIcon icon={faSearch} size="3x" className="text-muted" />
              </div>
              <h5>Nenhum município encontrado</h5>
              <p className="text-muted">
                {searchTerm
                  ? "Tente usar outro termo de busca"
                  : "Digite para pesquisar municípios"}
              </p>
            </div>
          ) : (
            <InfiniteLoader
              isItemLoaded={isItemLoaded}
              itemCount={itemCount}
              loadMoreItems={loadMoreItems}
              threshold={5}
            >
              {({ onItemsRendered, ref }: any) => (
                <List
                  className="List"
                  height={listHeight}
                  itemCount={itemCount}
                  itemSize={70}
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                  width="100%"
                >
                  {ItemRow}
                </List>
              )}
            </InfiniteLoader>
          )}
        </div>

        <div className="p-3 border-top mt-auto">
          <div className="d-flex justify-content-between">
            <Button variant="outline-secondary" onClick={onHide}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!selectedMunicipio}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
              Selecionar Município
            </Button>
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default MunicipioSelector;
